import { authentication } from '@microsoft/teams-js';
export class AuthError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'AuthError';
    }
}
/**
 * Handles Teams SSO token acquisition.
 * Token exchange is handled by AuthContext on the client side.
 */
export class TeamsAuthProvider {
    _cachedToken = null;
    _tokenExpiry = 0;
    /**
     * Get a Teams SSO token. Uses caching to avoid redundant calls.
     * Throws AuthError with descriptive code on failure.
     */
    async getSsoToken() {
        // Return cached token if still valid (with 5 min buffer)
        if (this._cachedToken && Date.now() < this._tokenExpiry - 5 * 60 * 1000) {
            return this._cachedToken;
        }
        try {
            // Race against a timeout — authentication.getAuthToken() can hang
            // indefinitely if Teams silently requires interactive consent.
            const timeoutMs = 8000;
            const token = await Promise.race([
                authentication.getAuthToken(),
                new Promise((_, reject) => setTimeout(() => reject(new Error(`Teams SSO timed out after ${timeoutMs}ms — interactive consent may be required`)), timeoutMs)),
            ]);
            this._cachedToken = token;
            this._tokenExpiry = this.extractExpiry(token);
            return token;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (message.includes('resourceRequiresConsent') ||
                message.includes('consent') ||
                message.includes('timed out')) {
                throw new AuthError(message, 'consent_required');
            }
            // Teams SDK not available (not in Teams iframe)
            if (message.includes('not initialized') || message.includes('not supported')) {
                throw new AuthError('Teams SDK not available', 'sdk_not_available');
            }
            throw new AuthError(`SSO token acquisition failed: ${message}`, 'token_failed');
        }
    }
    /**
     * Clear cached tokens.
     */
    clearCache() {
        this._cachedToken = null;
        this._tokenExpiry = 0;
    }
    /**
     * Extract expiry from JWT payload without verification.
     * Falls back to 1 hour if decoding fails.
     */
    extractExpiry(token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (typeof payload.exp === 'number') {
                return payload.exp * 1000; // seconds to ms
            }
        }
        catch {
            // Fall through to default
        }
        return Date.now() + 60 * 60 * 1000;
    }
}
//# sourceMappingURL=TeamsAuthProvider.js.map